import React from 'react';
import ReactDOM from 'react-dom';
import styles from './drag-sort.scss';
import { dragSortContext, dragItemContext, DragItemType, ItemContainer } from './drag-item';
import { useDragLayer } from 'react-dnd';
import useDiv from 'hellospa/common/hooks/use-div';


export default function DragLayer() {
  const dragRef = React.useRef<HTMLDivElement>(null);
  const contextValue = React.useMemo(() => ({
    dragRef,
    grab: () => undefined,
    item: {},
  }), []);

  const portalDiv = useDiv();
  const config = React.useContext(dragSortContext);

  const previewRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(function manageFocus() {
    if (previewRef.current) {
      previewRef.current.focus();
    }
  }/* Run on every render */);

  const dragInfo = useDragLayer((monitor) => {
    const item: DragItemType = monitor.getItem();
    if (item && item.type === config.type) {
      const diff = monitor.getDifferenceFromInitialOffset();

      if (diff) {
        return {
          preview: (
            <ItemContainer
              itemRef={previewRef}
              isGrabbed={true}
              grab={() => { }}
              style={{
                height: `${item.position.height}px`,
                width: `${item.position.width}px`,
                background: '#FFFFFF',
                opacity: 0.9,
              }}
            >
              {item.content}
            </ItemContainer>
          ),
          offset: {
            x: item.position.x + diff.x,
            y: item.position.y + diff.y,
          },
        };
      }
    }

    return null;
  });

  const isDragging = dragInfo != null;
  React.useLayoutEffect(function manageCursor() {
    // IE11: I can't use .toggle(styles.isDragging, isDragging) because IE11
    // doesn't support it.
    if (isDragging) {
      document.body.classList.add(styles.isDragging);
    } else {
      document.body.classList.remove(styles.isDragging);
    }

    return () => {
      // Always clean up when the component unmounts even though it generally
      // shouldn't be possible to unmount while dragging.
      document.body.classList.remove(styles.isDragging);
    };
  }, [isDragging]);

  if (!dragInfo) {
    return null;
  }

  const content = (
    <div className={styles.dragLayer} style={{
      top: dragInfo.offset.y,
      left: dragInfo.offset.x,
    }}
    >
      <dragItemContext.Provider value={contextValue}>
        {dragInfo.preview}
      </dragItemContext.Provider>
    </div>
  );

  return ReactDOM.createPortal(
    content,
    portalDiv,
  );
}
